import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { ComponentBase } from '@core/base/base.component.base';
import { DateFormatService } from '@core/date/date-format.service';
import { WidthObserverService } from '@roller/rdl/utilities/width-observer';
import { addMonths } from 'date-fns';
import { takeUntil, tap } from 'rxjs/operators';
import { CalendarEventService } from '../calendar-event.service';

@Component({
  selector: 'app-calendar-header',
  templateUrl: './calendar-header.component.html',
  styleUrls: ['./calendar-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarHeaderComponent extends ComponentBase {
  @Output() isMonthClicked: EventEmitter<void> = new EventEmitter<void>();
  isMobile = true;
  constructor(
    private matCalendar: MatCalendar<Date>,
    cdr: ChangeDetectorRef,
    private dateFormatService: DateFormatService,
    private calendarEventService: CalendarEventService,
    private widthObserverService: WidthObserverService
  ) {
    super();
    matCalendar.stateChanges
      .pipe(
        tap(() => cdr.markForCheck()),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
    this.widthObserverService
      .getIsMobile()
      .pipe(
        tap((result: boolean) => (this.isMobile = result)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  get periodLabel(): string {
    return this.dateFormatService.formatDateVenueLocale(this.matCalendar.activeDate, 'MMMM yyyy');
  }

  /** If month has nothing previous to today, disable the prev button. */
  prevButtonDisabled(): boolean {
    return this.dateFormatService.isSameMonth(this.matCalendar.activeDate, new Date());
  }

  /** The next button is disabled after two years - maximum dates. */
  nextButtonDisabled(): boolean {
    const blockedDate = this.dateFormatService.addYears(new Date(), 2);
    return this.dateFormatService.isSameMonth(this.matCalendar.activeDate, blockedDate);
  }

  /** Changes the calendar back one month. */
  previousClicked(ev: MouseEvent | TouchEvent | KeyboardEvent): void {
    this.setMonthClicked(ev);
    this.matCalendar.activeDate = addMonths(this.matCalendar.activeDate as Date, -1);
  }

  /** Changes the calendar forward one month. */
  nextClicked(ev: MouseEvent | TouchEvent | KeyboardEvent): void {
    this.setMonthClicked(ev);
    this.matCalendar.activeDate = addMonths(this.matCalendar.activeDate, 1);
  }

  /**
   * Determines if the event is a mouse click.
   * NOTE - Touch events for this intent are considered clicks - doesn't require a focus change.
   * @param ev emitted from button, can be mouse click, touch or keyboard enter/space.
   */
  setMonthClicked(ev: MouseEvent | TouchEvent | KeyboardEvent): void {
    const isTouch = (<MouseEvent>ev).clientX === undefined;
    this.calendarEventService.setIsMonthClicked(isTouch || (<MouseEvent>ev).clientX > 0);
  }

  /** Uses the calendar Event service to communicate with the calendar. */
  closeDialog(): void {
    this.calendarEventService.setCloseDialog(true);
  }
}
